import type { WorkDocument } from "@artemis-group/aiw-contracts";

export interface SavedLayPiece {
  kind: "startCut" | "fullBoard" | "endCut";
  serial: string | null;
  fromStartWallMM: number;
  lengthMM: number;
}

export interface SavedLayRow {
  rowNumber: number;
  measuredRunMM: number;
  widthMM: number;
  pieces: SavedLayPiece[];
  needsOnsiteSetOut: boolean;
}

export interface SavedLaySection {
  id: string;
  name: string;
  wallGapMM: number;
  boardLengthMM: number;
  boardWidthMM: number;
  savedLayDiagram: { longestMeasuredRunMM: number; rows: SavedLayRow[]; cutPairs: SavedCutPair[] };
}

export interface SavedCutPair {
  boardNumber: number;
  startSerial: string;
  startLengthMM: number;
  endSerial: string;
  endLengthMM: number;
  kerfMM: number;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const positive = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/** Show only the accepted Mobile layout. Never recalculate or display an unsaved reading. */
export function savedLaySections(document: WorkDocument | undefined): SavedLaySection[] {
  const groups = document?.simple_plank_sections;
  if (!record(groups)) return [];
  const result: SavedLaySection[] = [];
  for (const group of Object.values(groups)) {
    if (!Array.isArray(group)) continue;
    for (const raw of group) {
      if (!record(raw) || !record(raw.savedLayDiagram) ||
          !positive(raw.boardLengthMM) || !positive(raw.boardWidthMM) ||
          !positive(raw.savedLayDiagram.longestMeasuredRunMM) ||
          !Array.isArray(raw.savedLayDiagram.rows) ||
          typeof raw.id !== "string" || typeof raw.name !== "string" ||
          typeof raw.wallGapMM !== "number") continue;
      const rows: SavedLayRow[] = [];
      for (const row of raw.savedLayDiagram.rows) {
        if (!record(row) || !positive(row.rowNumber) || !positive(row.measuredRunMM) ||
            !positive(row.widthMM) || !Array.isArray(row.pieces) ||
            typeof row.needsOnsiteSetOut !== "boolean") break;
        const pieces: SavedLayPiece[] = [];
        for (const piece of row.pieces) {
          if (!record(piece) || !["startCut", "fullBoard", "endCut"].includes(String(piece.kind)) ||
              !positive(piece.lengthMM) || typeof piece.fromStartWallMM !== "number" ||
              !Number.isFinite(piece.fromStartWallMM) || piece.fromStartWallMM < 0 ||
              !(piece.serial === undefined || piece.serial === null || typeof piece.serial === "string")) break;
          pieces.push({ kind: piece.kind as SavedLayPiece["kind"],
            serial: typeof piece.serial === "string" ? piece.serial : null,
            fromStartWallMM: piece.fromStartWallMM, lengthMM: piece.lengthMM });
        }
        if (pieces.length !== row.pieces.length) break;
        rows.push({ rowNumber: row.rowNumber, measuredRunMM: row.measuredRunMM,
          widthMM: row.widthMM, pieces, needsOnsiteSetOut: row.needsOnsiteSetOut });
      }
      if (rows.length !== raw.savedLayDiagram.rows.length || !rows.length) continue;
      const cutPairs: SavedCutPair[] = [];
      if (Array.isArray(raw.savedLayDiagram.cutPairs)) {
        for (const pair of raw.savedLayDiagram.cutPairs) {
          if (!record(pair) || !positive(pair.boardNumber) ||
              typeof pair.startSerial !== "string" || typeof pair.endSerial !== "string" ||
              !positive(pair.startLengthMM) || !positive(pair.endLengthMM) ||
              typeof pair.kerfMM !== "number" || !Number.isFinite(pair.kerfMM) || pair.kerfMM < 0 ||
              Math.abs(pair.startLengthMM + pair.endLengthMM + pair.kerfMM - raw.boardLengthMM) > 0.01) continue;
          cutPairs.push(pair as unknown as SavedCutPair);
        }
      }
      result.push({ id: raw.id, name: raw.name, wallGapMM: raw.wallGapMM,
        boardLengthMM: raw.boardLengthMM, boardWidthMM: raw.boardWidthMM,
        savedLayDiagram: { longestMeasuredRunMM: raw.savedLayDiagram.longestMeasuredRunMM, rows, cutPairs } });
    }
  }
  return result;
}
